import {Outlet, useLocation} from 'react-router-dom'
import {ContentArea} from '@/components/shared/ContentArea'
import {KeyRound} from 'lucide-react'
import {useAuth} from '@/contexts/auth-context'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {InlineRow} from '@/components/shared/InlineRow'
import {SidebarNav, type SidebarNavItem} from '@/components/layout/SidebarNav'
import {SidebarNavId} from '@/testids/SidebarNavTestIds'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {CONCEPT_ICON} from '@/lib/concepts'
import {EmptyState} from '@/components/shared/EmptyState'
import {SectionContainer} from '@/components/shared/SectionContainer'

export function AccessGovernance() {
    const i18n = useI18n()
    const location = useLocation()
    const {hasPermission} = useAuth()
    const isRoot = location.pathname === routes.accessGovernance.root()
    usePageTitle(isRoot ? (i18n.accessGovernance.pageTitle as string) : null)

    const sectionAccess = i18n.accessGovernance.sectionAccess as string
    const sectionPolicies = i18n.accessGovernance.sectionPolicies as string
    const sectionCertifications = i18n.accessGovernance.sectionCertifications as string
    const sectionRestrictions = i18n.accessGovernance.sectionRestrictions as string
    const menuItems: (SidebarNavItem & { permission: string })[] = [
        {
            id: SidebarNavId.AccessProfiles,
            title: i18n.accessGovernance.accessProfiles as string,
            description: i18n.accessGovernance.accessProfilesDesc as string,
            href: routes.accessGovernance.profiles.list(),
            icon: CONCEPT_ICON.accessProfile,
            permission: permissions.accessProfiles.view,
            section: sectionAccess,
        },
        {
            id: SidebarNavId.FunctionGroups,
            title: i18n.accessGovernance.functionGroups as string,
            description: i18n.accessGovernance.functionGroupsDesc as string,
            href: routes.accessGovernance.functionGroups.list(),
            icon: CONCEPT_ICON['function-group'],
            permission: permissions.functionGroups.view,
            section: sectionAccess,
        },
        {
            id: SidebarNavId.AccessRequests,
            title: i18n.accessGovernance.accessRequests as string,
            description: i18n.accessGovernance.accessRequestsDesc as string,
            href: routes.accessGovernance.accessRequests.list(),
            icon: CONCEPT_ICON.accessRequest,
            permission: permissions.accessRequests.view,
            section: sectionAccess,
        },
        {
            id: SidebarNavId.FunctionGroupRequests,
            title: i18n.accessGovernance.functionGroupRequests as string,
            description: i18n.accessGovernance.functionGroupRequestsDesc as string,
            href: routes.accessGovernance.functionGroupRequests.list(),
            icon: CONCEPT_ICON.functionGroupRequest,
            permission: permissions.functionGroupRequests.view,
            section: sectionAccess,
        },
        {
            id: SidebarNavId.Delegations,
            title: i18n.accessGovernance.delegations as string,
            description: i18n.accessGovernance.delegationsDesc as string,
            href: routes.accessGovernance.delegations.list(),
            icon: CONCEPT_ICON.delegation,
            permission: permissions.delegations.view,
            section: sectionAccess,
        },
        {
            id: SidebarNavId.ApprovalPolicies,
            title: i18n.accessGovernance.approvalPolicies as string,
            description: i18n.accessGovernance.approvalPoliciesDesc as string,
            href: routes.accessGovernance.approvalPolicies.list(),
            icon: CONCEPT_ICON.approvalPolicy,
            permission: permissions.approvalPolicies.view,
            section: sectionPolicies,
        },
        {
            id: SidebarNavId.AutoAssignRules,
            title: i18n.accessGovernance.autoAssignRules as string,
            description: i18n.accessGovernance.autoAssignRulesDesc as string,
            href: routes.accessGovernance.rules.list(),
            icon: CONCEPT_ICON.autoAssignRule,
            permission: permissions.autoAssignRules.view,
            section: sectionPolicies,
        },
        {
            // WHY: runtime string id — escalation-thresholds nav entry, not yet
            // registered in the auto-generated SidebarNavId enum.
            // eslint-disable-next-line i18next/no-literal-string -- runtime nav id, not user-facing string
            id: 'escalation-thresholds',
            title: i18n.escalation.thresholds.title as string,
            description: i18n.escalation.thresholds.description as string,
            href: routes.accessGovernance.escalationThresholds.list(),
            icon: CONCEPT_ICON.approvalPolicy,
            permission: permissions.escalationThresholds.view,
            section: sectionPolicies,
        },
        {
            id: SidebarNavId.AutoAssignRuleRequests,
            title: i18n.accessGovernance.autoAssignRuleRequests as string,
            description: i18n.accessGovernance.autoAssignRuleRequestsDesc as string,
            href: routes.accessGovernance.autoAssignRuleRequests.list(),
            icon: CONCEPT_ICON.autoAssignRuleRequest,
            permission: permissions.autoAssignRules.view,
            section: sectionPolicies,
        },
        {
            id: SidebarNavId.RestrictionRules,
            title: i18n.accessGovernance.restrictionRules as string,
            description: i18n.accessGovernance.restrictionRulesDesc as string,
            href: routes.accessGovernance.restrictionRules.list(),
            icon: CONCEPT_ICON.restrictionRule,
            permission: permissions.restrictionRules.view,
            section: sectionRestrictions,
        },
        {
            id: SidebarNavId.RestrictionRuleRequests,
            title: i18n.accessGovernance.restrictionRuleRequests as string,
            description: i18n.accessGovernance.restrictionRuleRequestsDesc as string,
            href: routes.accessGovernance.restrictionRuleRequests.openOnly(),
            icon: CONCEPT_ICON.restrictionRuleRequest,
            permission: permissions.restrictionRuleRequests.view,
            section: sectionRestrictions,
        },
        {
            id: SidebarNavId.ViolationsDashboard,
            title: i18n.accessGovernance.violationsDashboard as string,
            description: i18n.accessGovernance.violationsDashboardDesc as string,
            href: routes.accessGovernance.violationsDashboard.path(),
            icon: CONCEPT_ICON.restrictionRuleViolation,
            permission: permissions.restrictionRules.view,
            section: sectionRestrictions,
        },
        {
            id: SidebarNavId.CertificationDashboard,
            title: i18n.accessGovernance.certificationDashboard as string,
            description: i18n.accessGovernance.certificationDashboardDesc as string,
            href: routes.accessGovernance.certificationDashboard.path(),
            icon: CONCEPT_ICON.certificationDashboard,
            permission: permissions.certifications.view,
            section: sectionCertifications,
        },
        {
            id: SidebarNavId.CertificationPolicies,
            title: i18n.accessGovernance.certificationPolicies as string,
            description: i18n.accessGovernance.certificationPoliciesDesc as string,
            href: routes.accessGovernance.certificationPolicies.list(),
            icon: CONCEPT_ICON.certificationCampaign,
            permission: permissions.certificationPolicies.view,
            section: sectionCertifications,
        },
    ]

    const visibleMenuItems = menuItems.filter(item => hasPermission(item.permission))

    return (
        <SectionContainer>
            <InlineRow align="start">
                <SidebarNav items={visibleMenuItems} aria-label={String(i18n.accessGovernance.pageTitle)}/>

                {/* Content Area */}
                <ContentArea>
                    {isRoot ? (
                        <EmptyState
                            variant="card"
                            icon={KeyRound}
                            title={String(i18n.accessGovernance.title)}
                            description={String(i18n.accessGovernance.selectOption)}
                            data-testid="access-governance-welcome-card"
                        />
                    ) : (
                        <Outlet/>
                    )}
                </ContentArea>
            </InlineRow>
        </SectionContainer>
    )
}
